import { Listbox, ListboxButton, ListboxOption, ListboxOptions } from '@headlessui/react';
import { ChevronDownIcon, XMarkIcon } from '@heroicons/react/20/solid';
import classNames from 'classnames';
import { useMemo, useState } from 'react';

type Option = { value: string; label: string };

export default function MultiSelectFilter({
  label,
  placeholder = '+ Add more filters',
  options,
  value,
  onChange,
  onOthersPick,
  othersValue = 'others',
  error,
  helperText,
  readOnly,
  fullWidth = true,
}: {
  label?: string;
  placeholder?: string;
  options: Option[];
  value: string[];
  onChange: (next: string[]) => void;
  onOthersPick?: () => void;
  /** Option value that triggers `onOthersPick` and is not added as a chip. */
  othersValue?: string;
  error?: string;
  helperText?: string;
  readOnly?: boolean;
  fullWidth?: boolean;
}) {
  const [resetKey, setResetKey] = useState(0);

  const available = useMemo(
    () => options.filter(o => o.value !== othersValue && !value.includes(o.value)),
    [options, value, othersValue]
  );

  const othersOption = useMemo(
    () => options.find(o => o.value === othersValue),
    [options, othersValue]
  );

  const showOthersInDropdown = Boolean(othersOption);

  const handlePick = (picked: string) => {
    if (picked === othersValue) {
      onOthersPick?.();
      setResetKey(k => k + 1);
      return;
    }
    if (!value.includes(picked)) {
      onChange([...value, picked]);
    }
    setResetKey(k => k + 1);
  };

  const remove = (v: string) => {
    onChange(value.filter(x => x !== v));
  };

  const labelByValue = useMemo(() => {
    const m = new Map<string, string>();
    options.forEach(o => m.set(o.value, o.label));
    return m;
  }, [options]);

  const getLabel = (v: string) => labelByValue.get(v) ?? v;

  if (readOnly) {
    return (
      <div>
        {label ? (
          <span className="block text-sm font-semibold leading-6 text-gray-900">{label}</span>
        ) : null}
        <div className="mt-2 flex flex-wrap gap-2">
          {value.map(v => (
            <span
              key={v}
              className="inline-flex items-center rounded-full border border-gray-200 bg-white px-3 py-1 text-sm text-gray-900"
            >
              {getLabel(v)}
            </span>
          ))}
        </div>
        {helperText && !error ? <p className="mt-2 text-sm text-gray-500">{helperText}</p> : null}
      </div>
    );
  }

  const dropdownOptions = [
    ...available,
    ...(showOthersInDropdown && othersOption ? [othersOption] : []),
  ];

  return (
    <div className="rounded-lg bg-gray-50 p-4">
      {label ? (
        <span className="mb-3 block text-sm font-semibold leading-6 text-gray-900">{label}</span>
      ) : null}

      <div className="space-y-3">
        <Listbox key={resetKey} value={null} onChange={handlePick}>
          <ListboxButton
            disabled={dropdownOptions.length === 0}
            className={classNames(
              'relative block rounded-lg border border-gray-300 bg-white py-1.5 pr-8 pl-3 text-left text-sm/6 text-black',
              {
                'w-full': fullWidth,
                'cursor-not-allowed opacity-50': dropdownOptions.length === 0,
                'ring-1 ring-red-300 focus:ring-red-600': error,
              }
            )}
          >
            <span className="block text-gray-500">{placeholder}</span>
            <ChevronDownIcon
              className="pointer-events-none absolute top-2.5 right-2.5 size-4 fill-gray-500"
              aria-hidden="true"
            />
          </ListboxButton>
          <ListboxOptions
            anchor="top"
            transition
            className={classNames(
              'w-[var(--button-width)] rounded-lg border border-gray-300 bg-white p-1 [--anchor-gap:var(--spacing-1)] focus:outline-none',
              'transition duration-100 ease-in data-[leave]:data-[closed]:opacity-0 z-50 mb-2'
            )}
          >
            {dropdownOptions.map((option, idx) => (
              <ListboxOption
                key={option.value + idx}
                value={option.value}
                className="group flex cursor-default items-center gap-2 rounded-lg py-1.5 px-3 select-none data-[focus]:bg-gray-50"
              >
                <span className="block text-sm text-gray-900">{option.label}</span>
              </ListboxOption>
            ))}
          </ListboxOptions>
        </Listbox>

        {value.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {value.map(v => (
              <span
                key={v}
                className="inline-flex items-center gap-1 rounded-xl border border-gray-200 bg-gray-300 py-1 pl-3 pr-1 text-sm font-medium text-gray-900 shadow-sm"
              >
                {getLabel(v)}
                <button
                  type="button"
                  className="rounded-full p-0.5 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                  onClick={() => remove(v)}
                  aria-label={`Remove ${getLabel(v)}`}
                >
                  <XMarkIcon className="size-6" />
                </button>
              </span>
            ))}
          </div>
        ) : null}
      </div>

      {helperText && !error ? <p className="mt-2 text-sm text-gray-500">{helperText}</p> : null}
      {error ? (
        <p className="mt-2 text-sm text-red-600" id="error">
          {error}
        </p>
      ) : null}
    </div>
  );
}
